import type { MillConstants } from "@/lib/mill/constants";
import { DEFAULT_MILL_CONSTANTS } from "@/lib/mill/constants";

/** Tube weight in grams: (OD-t)*t*factor*length_mm */
export function tubeWeightGrams(
  od: number,
  thickness: number,
  lengthMm: number,
  factor = DEFAULT_MILL_CONSTANTS.tube_weight_factor,
): number {
  return (od - thickness) * thickness * factor * lengthMm;
}

export function tubeWeightTons(
  od: number,
  thickness: number,
  lengthMm: number,
  qty: number,
  factor = DEFAULT_MILL_CONSTANTS.tube_weight_factor,
): number {
  return (tubeWeightGrams(od, thickness, lengthMm, factor) * qty) / 1_000_000;
}

export function tubeQtyFromWeight(
  weightTons: number,
  od: number,
  thickness: number,
  lengthMm: number,
  factor = DEFAULT_MILL_CONSTANTS.tube_weight_factor,
): number {
  const single = tubeWeightGrams(od, thickness, lengthMm, factor) / 1_000_000;
  if (single <= 0) return 0;
  return weightTons / single;
}

export type CtlCut = { lengthMm: number; type: "piece" | "blade" };

export type CtlNestResult = {
  cuts: CtlCut[];
  pieces: number;
  bladesLength: number;
  usedLength: number;
  leftover: number;
  requiredWithMargin: number;
};

/** Nest piece lengths on mother tube length with blade kerf between cuts. */
export function nestCtl(input: {
  motherLengthMm: number;
  pieceLengths: number[];
  bladeMm?: number;
  yieldMarginPct?: number;
  constants?: Partial<MillConstants>;
}): CtlNestResult {
  const c = { ...DEFAULT_MILL_CONSTANTS, ...input.constants };
  const blade = input.bladeMm ?? c.ctl_blade_mm;
  const margin = (input.yieldMarginPct ?? c.ctl_yield_margin_pct) / 100;
  const pieces = [...input.pieceLengths].filter((l) => l > 0).sort((a, b) => b - a);
  const demand = pieces.reduce((s, l) => s + l, 0);
  const requiredWithMargin = demand * (1 + margin);

  const cuts: CtlCut[] = [];
  let used = 0;
  let bladesLength = 0;
  let placed = 0;

  for (let i = 0; i < pieces.length; i++) {
    const len = pieces[i];
    const needBlade = placed > 0 ? blade : 0;
    if (used + needBlade + len > input.motherLengthMm + 1e-6) break;
    if (needBlade > 0) {
      cuts.push({ lengthMm: blade, type: "blade" });
      used += blade;
      bladesLength += blade;
    }
    cuts.push({ lengthMm: len, type: "piece" });
    used += len;
    placed += 1;
  }

  return {
    cuts,
    pieces: placed,
    bladesLength,
    usedLength: used,
    leftover: Math.max(0, input.motherLengthMm - used),
    requiredWithMargin,
  };
}

/** Sheet cut planner: how many sheets of L×W from coil area (simple). */
export function planSheetCuts(input: {
  coilWidth: number;
  coilLengthEstimateMm: number;
  sheetWidth: number;
  sheetLength: number;
  qty: number;
}): { sheets: number; scrapAreaPct: number; fits: boolean } {
  if (input.sheetWidth <= 0 || input.sheetLength <= 0) {
    return { sheets: 0, scrapAreaPct: 100, fits: false };
  }
  const across = Math.floor(input.coilWidth / input.sheetWidth);
  if (across < 1) return { sheets: 0, scrapAreaPct: 100, fits: false };
  const rowsNeeded = Math.ceil(input.qty / across);
  const lengthNeeded = rowsNeeded * input.sheetLength;
  const fits = lengthNeeded <= input.coilLengthEstimateMm + 1e-6;
  const usedArea = input.qty * input.sheetWidth * input.sheetLength;
  const coilArea = input.coilWidth * Math.max(lengthNeeded, input.coilLengthEstimateMm);
  const scrapAreaPct = coilArea > 0 ? Math.max(0, (1 - usedArea / coilArea) * 100) : 100;
  return { sheets: input.qty, scrapAreaPct, fits };
}
